import {
  COUNTRY_CODE,
  DOMESTIC_NUTRITION_KEYS,
} from "../../../Utils/constants.js"
import PHILIPPINES_RENI_VALUES from "../../../Utils/philippinesNutrientReniValues"
import {
  DOMESTIC_TO_PHILIPPINES_AGE_GROUP_MAPPING,
  PHILIPPINES_MANDATORY_NUTRIENTS,
  PHILIPPINES_NON_MANDATORY_NUTRIENTS,
  PhilippinesNiTableCalculatedValue,
} from "../../../schema/PhilippinesSchema/philippinesConstants"

interface NameQuantityItem {
  name: string
  quantity: string | number
}

export function arrayToObj(
  arr: NameQuantityItem[],
): Record<string, string | number> {
  const obj: Record<string, string | number> = {}
  arr.forEach((item) => {
    if (!item.name) return
    obj[item.name] = item.quantity
  })
  return obj
}

function convertToBaseUnit(quantity: number, unit: string): number {
  if (!unit) return 0
  switch (unit.toLowerCase()) {
    case "kg":
    case "l":
      return quantity * 1000
    case "g":
    case "ml":
      return quantity
    default:
      return 0
  }
}

const B_VITAMIN_KEYS = [
  DOMESTIC_NUTRITION_KEYS.VITAMIN_B1?.toLowerCase?.(),
  DOMESTIC_NUTRITION_KEYS.VITAMIN_B2?.toLowerCase?.(),
  DOMESTIC_NUTRITION_KEYS.VITAMIN_B3?.toLowerCase?.(),
  DOMESTIC_NUTRITION_KEYS.PANTOTHENIC_ACID?.toLowerCase?.(),
  DOMESTIC_NUTRITION_KEYS.VITAMIN_B6?.toLowerCase?.(),
  DOMESTIC_NUTRITION_KEYS.BIOTIN?.toLowerCase?.(),
  DOMESTIC_NUTRITION_KEYS.FOLIC_ACID?.toLowerCase?.(),
  DOMESTIC_NUTRITION_KEYS.VITAMIN_B12?.toLowerCase?.(),
].filter(Boolean)

const FATS_AND_CHOLES_KEYS = [
  PHILIPPINES_MANDATORY_NUTRIENTS.CHOLESTEROL?.toLowerCase?.(),
  PHILIPPINES_MANDATORY_NUTRIENTS.SATURATED_FAT?.toLowerCase?.(),
  PHILIPPINES_MANDATORY_NUTRIENTS.TRANS_FAT?.toLowerCase?.(),
].filter(Boolean)

function needsTwoDecimals(nutrientKey: string): boolean {
  const lowerKey = nutrientKey?.toLowerCase()
  if (B_VITAMIN_KEYS.includes(lowerKey)) return true
  if (FATS_AND_CHOLES_KEYS.includes(lowerKey)) return true
  return false
}

export function formatPerServeValue(
  nutrientKey: string,
  rawValue: number,
): string {
  if (needsTwoDecimals(nutrientKey)) {
    return rawValue.toFixed(2)
  } else {
    return Math.round(rawValue).toString()
  }
}

export function formatPercentReniValue(rawValue: number): string {
  if (!isFinite(rawValue) || rawValue <= 0) {
    return "-"
  }
  if (rawValue < 2) {
    return "<2%"
  }

  return Math.round(rawValue).toString()
}

function basicPerServeCalc(per100: number, servingSize: number): number {
  return (per100 / 100) * servingSize
}

export function calculateServings(
  serving_size: number,
  serving_size_unit: string,
  net_quantity: number,
  net_quantity_unit: string,
): number {
  const serving_size_in_base = convertToBaseUnit(
    serving_size,
    serving_size_unit,
  )
  const net_quantity_in_base = convertToBaseUnit(
    net_quantity,
    net_quantity_unit,
  )

  if (!serving_size_in_base || !net_quantity_in_base) {
    return 0
  }

  const no_of_servings = net_quantity_in_base / serving_size_in_base
  const fraction = no_of_servings - Math.floor(no_of_servings)
  return fraction >= 0.6
    ? Math.ceil(no_of_servings)
    : Math.floor(no_of_servings)
}

export function getPhilippinesNiTableCalculatedValue(
  label_json: any,
): PhilippinesNiTableCalculatedValue {
  if (!label_json) {
    return {
      serving_size: 0,
      country: COUNTRY_CODE.PHILIPPINES,
      serving_size_unit: "",
      no_of_servings: 0,
      age_group: label_json?.age_group,
      calories: 0,
      calories_from_fat: 0,
      calories_reni_value: 0,
      mandatory_nutrient_values: {
        ENERGY: {
          key: PHILIPPINES_MANDATORY_NUTRIENTS.ENERGY,
          name: "Energy (kcal)",
          per_serve: 0,
          reni_value: 0,
        },
        TOTAL_CARBOHYDRATES: {
          key: PHILIPPINES_MANDATORY_NUTRIENTS.TOTAL_CARBOHYDRATE,
          name: "Total Carbohydrates (g)",
          per_serve: 0,
          reni_value: 0,
        },
        SUGAR: {
          key: PHILIPPINES_MANDATORY_NUTRIENTS.TOTAL_SUGARS,
          name: "Sugar (g)",
          per_serve: 0,
          reni_value: 0,
        },
        TOTAL_PROTEIN: {
          key: PHILIPPINES_MANDATORY_NUTRIENTS.PROTEIN,
          name: "Total Protein (g)",
          per_serve: 0,
          reni_value: 0,
        },
        TOTAL_FAT: {
          key: PHILIPPINES_MANDATORY_NUTRIENTS.TOTAL_FAT,
          name: "Total Fat (g)",
          per_serve: 0,
          reni_value: 0,
        },
        SATURATED_FAT: {
          key: PHILIPPINES_MANDATORY_NUTRIENTS.SATURATED_FAT,
          name: "Saturated Fat (g)",
          per_serve: 0,
          reni_value: 0,
        },
        SODIUM: {
          key: PHILIPPINES_MANDATORY_NUTRIENTS.SODIUM,
          name: "Sodium (mg)",
          per_serve: 0,
          reni_value: 0,
        },
        CHOLESTEROL: {
          key: PHILIPPINES_MANDATORY_NUTRIENTS.CHOLESTEROL,
          name: "Cholesterol (mg)",
          per_serve: 0,
          reni_value: 0,
        },
        TRANS_FAT: {
          key: PHILIPPINES_MANDATORY_NUTRIENTS.TRANS_FAT,
          name: "Trans Fat (g)",
          per_serve: 0,
          reni_value: 0,
        },
        DIETARY_FIBER: {
          key: PHILIPPINES_MANDATORY_NUTRIENTS.DIETARY_FIBRE,
          name: "Dietary Fiber (g)",
          per_serve: 0,
          reni_value: 0,
        },
      },
      non_mandatory_nutrient_values: [],
    }
  }

  const number_of_servings = calculateServings(
    parseFloat(label_json?.serving_size ?? "0"),
    label_json?.serving_size_unit ?? "",
    parseFloat(label_json?.net_quantity ?? "0"),
    label_json?.net_quantity_unit ?? "",
  )

  const target_audience_age =
    label_json?.age_group as keyof typeof DOMESTIC_TO_PHILIPPINES_AGE_GROUP_MAPPING

  let philippines_agewise_reni_values: any = {}
  if (PHILIPPINES_RENI_VALUES && target_audience_age) {
    const mapped_age_group =
      DOMESTIC_TO_PHILIPPINES_AGE_GROUP_MAPPING[target_audience_age]
    if (
      mapped_age_group &&
      PHILIPPINES_RENI_VALUES[
        mapped_age_group as keyof typeof PHILIPPINES_RENI_VALUES
      ]
    ) {
      philippines_agewise_reni_values = {
        ...PHILIPPINES_RENI_VALUES[
          mapped_age_group as keyof typeof PHILIPPINES_RENI_VALUES
        ],
      }
    }
  }

  const non_mandatory_nutrients = arrayToObj(
    label_json?.non_mandatory_nutrient ?? [],
  )
  const serving_size_num = parseFloat(label_json?.serving_size ?? "0")

  const non_mandatory_nutrient_values =
    PHILIPPINES_NON_MANDATORY_NUTRIENTS.filter((nm) => {
      const per_100 = parseFloat(
        non_mandatory_nutrients[nm.key?.toLowerCase()]?.toString() || "0",
      )
      return per_100 !== 0
    }).map((nm) => {
      const per_100 = parseFloat(
        non_mandatory_nutrients[nm.key?.toLowerCase()]?.toString() || "0",
      )
      const rawPerServe = basicPerServeCalc(per_100, serving_size_num)

      const recommended_value = parseFloat(
        philippines_agewise_reni_values[nm.key?.toLowerCase()] ?? "0",
      )

      let rawDv = -1
      if (recommended_value > 0) {
        rawDv = per_100 * (serving_size_num / recommended_value)
      }

      const formattedPerServe = formatPerServeValue(nm.key, rawPerServe)

      const formattedReni = rawDv > 0 ? formatPercentReniValue(rawDv) : "-"

      return {
        key: nm.key,
        name: nm.value,
        per_serve: formattedPerServe,
        reni_value: formattedReni,
      }
    })

  const mandatory_nutrient_arr = label_json?.mandatory_nutrient ?? []
  const firstMandatory = mandatory_nutrient_arr[0] || {}

  const fat_value = parseFloat(
    firstMandatory[PHILIPPINES_MANDATORY_NUTRIENTS.TOTAL_FAT] ?? "0",
  )
  const energy_value = parseFloat(
    firstMandatory[PHILIPPINES_MANDATORY_NUTRIENTS.ENERGY] ?? "0",
  )

  let rawCalDv = -1
  const energyRec = parseFloat(
    philippines_agewise_reni_values[PHILIPPINES_MANDATORY_NUTRIENTS.ENERGY] ??
      "0",
  )
  if (energyRec > 0) {
    rawCalDv = energy_value * (serving_size_num / energyRec)
  }
  const calories_reni_val_str =
    rawCalDv > 0 ? formatPercentReniValue(rawCalDv) : "-"

  function getMandatoryRawPerServe(nutKey: string): number {
    const val = parseFloat(firstMandatory[nutKey] ?? "0")
    return basicPerServeCalc(val, serving_size_num)
  }

  function getMandatoryRawDv(nutKey: string): number {
    const val = parseFloat(firstMandatory[nutKey] ?? "0")
    const recommendedVal = parseFloat(
      philippines_agewise_reni_values[nutKey.toLowerCase()] ?? "0",
    )
    if (recommendedVal > 0) {
      return val * (serving_size_num / recommendedVal)
    }
    return -1
  }

  function getMandatoryPerServe(nutKey: string): string {
    return formatPerServeValue(nutKey, getMandatoryRawPerServe(nutKey))
  }

  function getMandatoryReniValue(nutKey: string): string {
    const dv = getMandatoryRawDv(nutKey)
    return dv > 0 ? formatPercentReniValue(dv) : "-"
  }

  return {
    serving_size: serving_size_num,
    country: COUNTRY_CODE.PHILIPPINES,
    serving_size_unit: label_json?.serving_size_unit ?? "",
    no_of_servings: number_of_servings,
    age_group: label_json?.age_group ?? "",
    calories: Math.round(basicPerServeCalc(energy_value, serving_size_num)),
    calories_from_fat:
      Math.round(basicPerServeCalc(fat_value, serving_size_num)) * 9,
    calories_reni_value: calories_reni_val_str,

    mandatory_nutrient_values: {
      ENERGY: {
        key: PHILIPPINES_MANDATORY_NUTRIENTS.ENERGY,
        name: "Energy (kcal)",
        per_serve: getMandatoryPerServe(PHILIPPINES_MANDATORY_NUTRIENTS.ENERGY),
        reni_value: getMandatoryReniValue(
          PHILIPPINES_MANDATORY_NUTRIENTS.ENERGY,
        ),
      },
      TOTAL_CARBOHYDRATES: {
        key: PHILIPPINES_MANDATORY_NUTRIENTS.TOTAL_CARBOHYDRATE,
        name: "Total Carbohydrates (g)",
        per_serve: getMandatoryPerServe(
          PHILIPPINES_MANDATORY_NUTRIENTS.TOTAL_CARBOHYDRATE,
        ),
        reni_value: "-",
      },
      SUGAR: {
        key: PHILIPPINES_MANDATORY_NUTRIENTS.TOTAL_SUGARS,
        name: "Sugar (g)",
        per_serve: getMandatoryPerServe(
          PHILIPPINES_MANDATORY_NUTRIENTS.TOTAL_SUGARS,
        ),
        reni_value: "-",
      },
      TOTAL_PROTEIN: {
        key: PHILIPPINES_MANDATORY_NUTRIENTS.PROTEIN,
        name: "Total Protein (g)",
        per_serve: getMandatoryPerServe(
          PHILIPPINES_MANDATORY_NUTRIENTS.PROTEIN,
        ),
        reni_value: getMandatoryReniValue(
          PHILIPPINES_MANDATORY_NUTRIENTS.PROTEIN,
        ),
      },
      TOTAL_FAT: {
        key: PHILIPPINES_MANDATORY_NUTRIENTS.TOTAL_FAT,
        name: "Total Fat (g)",
        per_serve: getMandatoryPerServe(
          PHILIPPINES_MANDATORY_NUTRIENTS.TOTAL_FAT,
        ),
        reni_value: getMandatoryReniValue(
          PHILIPPINES_MANDATORY_NUTRIENTS.TOTAL_FAT,
        ),
      },
      SATURATED_FAT: {
        key: PHILIPPINES_MANDATORY_NUTRIENTS.SATURATED_FAT,
        name: "Saturated Fat (g)",
        per_serve: getMandatoryPerServe(
          PHILIPPINES_MANDATORY_NUTRIENTS.SATURATED_FAT,
        ),
        reni_value: "-",
      },
      SODIUM: {
        key: PHILIPPINES_MANDATORY_NUTRIENTS.SODIUM,
        name: "Sodium (mg)",
        per_serve: getMandatoryPerServe(PHILIPPINES_MANDATORY_NUTRIENTS.SODIUM),
        reni_value: getMandatoryReniValue(
          PHILIPPINES_MANDATORY_NUTRIENTS.SODIUM,
        ),
      },
      CHOLESTEROL: {
        key: PHILIPPINES_MANDATORY_NUTRIENTS.CHOLESTEROL,
        name: "Cholesterol (mg)",
        per_serve: getMandatoryPerServe(
          PHILIPPINES_MANDATORY_NUTRIENTS.CHOLESTEROL,
        ),
        reni_value: "-",
      },
      TRANS_FAT: {
        key: PHILIPPINES_MANDATORY_NUTRIENTS.TRANS_FAT,
        name: "Trans Fat (g)",
        per_serve: getMandatoryPerServe(
          PHILIPPINES_MANDATORY_NUTRIENTS.TRANS_FAT,
        ),
        reni_value: "-",
      },
      DIETARY_FIBER: {
        key: PHILIPPINES_MANDATORY_NUTRIENTS.DIETARY_FIBRE,
        name: "Dietary Fiber (g)",
        per_serve: getMandatoryPerServe(
          PHILIPPINES_MANDATORY_NUTRIENTS.DIETARY_FIBRE,
        ),
        reni_value: getMandatoryReniValue(
          PHILIPPINES_MANDATORY_NUTRIENTS.DIETARY_FIBRE,
        ),
      },
    },

    non_mandatory_nutrient_values,
  }
}
